import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Ji, ma } from './ArenaMedia.jsx';
import { de } from './ArenaDialog.jsx';
import { DRAG_THRESHOLD, isTableDrop } from './hand-gesture.js';
import { ArenaBattleCard } from './ArenaBattleCard.jsx';

const SETTLE_MS = 240;
const dragTransform = (x, y, angle = 0, scale = 1) =>
  `translate3d(${x}px, ${y}px, 0) translate(-50%, -75%) rotate(${angle}deg) scale(${scale})`;

export function ArenaBattleHand({ cards, selected, onSelect, canPlay, onPlay, tableRef, onDragChange, intro }) {
  const handRef = useRef(null);
  const floating = useRef(null);
  const gesture = useRef(null);
  const motion = useRef(null);
  const frame = useRef(null);
  const settling = useRef(null);
  const suppressClick = useRef(false);
  const [drag, setDrag] = useState(null);
  const availableSlots = cards.filter(card => !card.used).map(card => card.slot).join(',');
  const selectedCard = cards.find(card => card.slot === selected && !card.used);

  function stopMotion() {
    window.cancelAnimationFrame(frame.current);
    frame.current = null;
  }

  function clearDrag() {
    stopMotion();
    gesture.current = null;
    motion.current = null;
    setDrag(null);
    onDragChange(false);
  }

  useEffect(() => {
    // A submitted card may disappear from the hand before its visual landing ends.
    if (!settling.current) clearDrag();
  }, [availableSlots, canPlay, onDragChange]);
  useEffect(() => () => {
    window.cancelAnimationFrame(frame.current);
    window.clearTimeout(settling.current?.timer);
    settling.current?.animation?.cancel();
  }, []);

  function followPointer() {
    stopMotion();
    let previousTime;
    function tick(time) {
      const current = motion.current;
      if (!current || !gesture.current) return;
      const elapsed = previousTime === undefined ? 16 : Math.min(40, time - previousTime);
      previousTime = time;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const blend = reduced ? 1 : 1 - Math.exp(-elapsed / 38);
      current.x += (current.targetX - current.x) * blend;
      current.y += (current.targetY - current.y) * blend;
      current.angle += (current.targetAngle - current.angle) * blend;
      current.targetAngle *= .9;
      current.scale += ((reduced ? 1 : 1.06) - current.scale) * blend;
      if (floating.current) floating.current.style.transform = dragTransform(current.x, current.y, reduced ? 0 : current.angle, current.scale);
      frame.current = window.requestAnimationFrame(tick);
    }
    frame.current = window.requestAnimationFrame(tick);
  }

  function settle(submit) {
    const current = gesture.current;
    const position = motion.current;
    if (!current || !position || !floating.current) { clearDrag(); return; }
    stopMotion();
    gesture.current = null;
    onDragChange(false);
    const element = floating.current;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const source = current.element.getBoundingClientRect();
    let x = source.x + source.width / 2;
    let y = source.y + source.height * .75;
    let scale = source.width / element.offsetWidth;
    if (submit) {
      const table = tableRef.current;
      const bounds = table.getBoundingClientRect();
      const cellWidth = (bounds.width - 20) / 5;
      const width = Math.min(cellWidth, Math.max(1, bounds.height - 21) * current.ratio);
      const height = width / current.ratio + 21;
      x = bounds.left + (cellWidth + 5) * table.querySelectorAll('.battle-card').length + cellWidth / 2;
      y = bounds.top + bounds.height / 2 + height * .25;
      scale = width / element.offsetWidth;
    }
    setDrag(previous => previous && { ...previous, phase: submit ? 'landing' : 'returning', over: false });
    const duration = reduced ? 0 : SETTLE_MS;
    const animation = element.animate([
      { transform: element.style.transform || dragTransform(position.x, position.y, position.angle, position.scale), opacity: 1 },
      { transform: dragTransform(x, y, 0, scale), opacity: submit ? 0 : 1 },
    ], { duration, easing: 'cubic-bezier(.2,.75,.25,1)', fill: 'forwards' });
    const timer = window.setTimeout(() => {
      animation.cancel();
      settling.current = null;
      clearDrag();
    }, duration);
    settling.current = { animation, timer };
    // Send immediately: animation must not delay a timed turn.
    if (submit) onPlay(current.card.slot);
  }

  function cancel() {
    if (gesture.current?.moved) settle(false);
    else if (gesture.current) clearDrag();
  }

  function finish(event) {
    const current = gesture.current;
    if (!current || current.pointerId !== event.pointerId) return;
    const submit = current.moved && canPlay && isTableDrop({ x: event.clientX, y: event.clientY }, tableRef.current?.getBoundingClientRect());
    suppressClick.current = current.moved;
    if (current.moved) settle(submit);
    else clearDrag();
  }

  return <section ref={handRef} className="battle-hand" aria-label="Ваши карты" inert={intro ? true : undefined}>
    {selectedCard && !drag && <div className="battle-hand-inspect" role="status">
      <b>{selectedCard.modifier || selectedCard.name}</b>
      <span>Урон {ma(selectedCard.damage)} · {canPlay ? 'Перетащите на поле' : 'Ход противника'}</span>
    </div>}
    <div className="battle-hand-cards">
      {cards.filter(card => !card.used).map((card, index, hand) => <ArenaBattleCard card={card} type="button" key={card.slot}
        className={`battle-card battle-hand-card ${de[Ji(card.rarity)] || ''}${selected === card.slot ? ' battle-hand-selected' : ''}${drag?.card.slot === card.slot ? ' battle-hand-lifted' : ''}`}
        style={{ '--hand-angle': `${(index - (hand.length - 1) / 2) * 3}deg`,
          '--hand-focus-x': hand.length > 1 ? index === 0 ? '16px' : index === hand.length - 1 ? '-16px' : '0px' : '0px' }}
        aria-pressed={selected === card.slot} aria-label={`${card.name}. ${card.modifier || 'Без модификатора'}. Урон ${ma(card.damage)}`}
        onClick={() => { if (!suppressClick.current) onSelect(card.slot); suppressClick.current = false; }}
        onPointerDown={event => {
          if (!event.isPrimary || event.button !== 0 || settling.current) return;
          suppressClick.current = false;
          event.currentTarget.setPointerCapture(event.pointerId);
          gesture.current = { card, element: event.currentTarget, pointerId: event.pointerId,
            ratio: Number(getComputedStyle(event.currentTarget).getPropertyValue('--battle-art-ratio')) || .62,
            x: event.clientX, y: event.clientY, lastX: event.clientX, moved: false, over: false };
          onSelect(card.slot);
        }}
        onPointerMove={event => {
          const current = gesture.current;
          if (!current || current.pointerId !== event.pointerId || !canPlay) return;
          if (!current.moved && Math.hypot(event.clientX - current.x, event.clientY - current.y) <= DRAG_THRESHOLD) return;
          const over = isTableDrop({ x: event.clientX, y: event.clientY }, tableRef.current?.getBoundingClientRect());
          if (!current.moved) {
            current.moved = true;
            const bounds = current.element.getBoundingClientRect();
            motion.current = { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height * .75,
              targetX: event.clientX, targetY: event.clientY, angle: 0, targetAngle: 0, scale: 1 };
            setDrag({ card, width: bounds.width, ratio: current.ratio, phase: 'dragging', over });
            followPointer();
          }
          motion.current.targetX = event.clientX;
          motion.current.targetY = event.clientY;
          motion.current.targetAngle = Math.max(-9, Math.min(9, (event.clientX - current.lastX) * .3));
          current.lastX = event.clientX;
          if (current.over !== over) {
            current.over = over;
            setDrag(previous => previous && { ...previous, over });
          }
          onDragChange(over);
        }} onPointerUp={finish} onPointerCancel={cancel} onLostPointerCapture={cancel}>
        <span className="battle-slot">{card.slot}</span>
      </ArenaBattleCard>)}
    </div>
    {drag && createPortal(<ArenaBattleCard ref={floating} as="div" card={drag.card} initialRatio={drag.ratio}
      className={`battle-card battle-hand-drag battle-hand-${drag.phase} ${de[Ji(drag.card.rarity)] || ''}${drag.over ? ' battle-hand-over' : ''}`}
      style={{ width: drag.width, left: 0, top: 0,
        transform: dragTransform(motion.current?.x || 0, motion.current?.y || 0, motion.current?.angle || 0, motion.current?.scale || 1) }} aria-hidden="true" />,
    handRef.current.closest('dialog'))}
  </section>;
}
